package it.unibo.tuprolog.solve.flags

import it.unibo.tuprolog.core.Term
import it.unibo.tuprolog.solve.Helpable
import kotlin.js.JsName
import kotlin.jvm.JvmStatic

/**
 * A typed, well-known Prolog flag, i.e. a named switch whose legal values and default are known ahead of time --
 * as opposed to the arbitrary `String → `[Term]` entries a plain [FlagStore] can also hold. Implemented as `object`s
 * for both ISO-standard flags ([Unknown], [DoubleQuotes], [MaxArity]) and implementation-specific ones
 * ([LastCallOptimization], [TrackVariables]).
 *
 * @see FlagStore
 */
interface NotableFlag : Helpable {
    /** This flag's name, as it appears as the key in a [FlagStore] (e.g. `"unknown"`, `"double_quotes"`). */
    @JsName("name")
    val name: String

    /** The value this flag takes unless explicitly set otherwise. */
    @JsName("defaultValue")
    val defaultValue: Term

    /** Whether this flag's value can be changed at all (`false` for e.g. [MaxArity]). */
    @JsName("isEditable")
    val isEditable: Boolean
        get() = true

    /** Every legal value this flag may take. */
    @JsName("admissibleValues")
    val admissibleValues: FlagDomain

    /** Markdown documentation exposed through `help(flag(Name), Help)`. */
    override val help: String
        get() =
            buildString {
                append("`flag(").append(name).append(")`\n\n")
                append("Solver flag `").append(name).append("`.\n\n")
                append("- Default: `").append(defaultValue).append("`\n")
                append("- Editable: `").append(isEditable).append("`\n")
                append("- Admissible values: ")
                when (val domain = admissibleValues) {
                    is FlagDomain.IntRange ->
                        append("`")
                            .append(domain.minInclusive)
                            .append("..")
                            .append(domain.maxInclusive)
                            .append("`")
                    else -> append(domain.joinToString(", ") { "`$it`" })
                }
            }

    /** Whether [value] is one of [admissibleValues]. */
    @JsName("isAdmissibleValue")
    fun isAdmissibleValue(value: Term): Boolean = value in admissibleValues

    /** This flag's [name] paired with its [defaultValue], ready to be inserted into a [FlagStore]. */
    @JsName("toPair")
    fun toPair(): Pair<String, Term> = this to defaultValue

    /**
     * This flag's [name] paired with [value], ready to be inserted into a [FlagStore].
     * @throws IllegalArgumentException if [value] is not among [admissibleValues].
     */
    @JsName("to")
    infix fun to(value: Term): Pair<String, Term> =
        name to
            value.also {
                require(isAdmissibleValue(it)) {
                    "$value is not an admissible value for flag $name"
                }
            }

    companion object {
        /** Looks up the built-in [NotableFlag] ([DoubleQuotes], [LastCallOptimization], [MaxArity], [TrackVariables],
         * [Unknown], [ShowWildCardVariablesInSolutions], [UniqueSolutions], [GroundQueriesHaveBooleanSolution])
         * named [name], or `null` if none matches. */
        @JsName("fromName")
        @JvmStatic
        fun fromName(name: String): NotableFlag? =
            sequenceOf(
                DoubleQuotes,
                LastCallOptimization,
                MaxArity,
                TrackVariables,
                Unknown,
                ShowWildCardVariablesInSolutions,
                UniqueSolutions,
                GroundQueriesHaveBooleanSolution,
            ).firstOrNull { it.name == name }
    }
}
